// Dynamic Markdown loader for blog posts
// Scans src/content/blog/*.md and src/assets/blog/* automatically

const mdContext = require.context('../content/blog', false, /\.md$/);
const imgContext = require.context('../assets/blog', false, /\.(png|jpe?g|svg|webp)$/);

let cachedPosts = null;
let loadingPromise = null;

/**
 * Parses markdown frontmatter (YAML-like block between --- and ---)
 */
export function parseFrontmatter(text) {
  if (!text || typeof text !== 'string') {
    return { metadata: {}, body: '' };
  }

  const trimmedText = text.trim();
  if (!trimmedText.startsWith('---')) {
    return { metadata: {}, body: trimmedText };
  }

  const endFrontmatterIndex = trimmedText.indexOf('---', 3);
  if (endFrontmatterIndex === -1) {
    return { metadata: {}, body: trimmedText };
  }

  const frontmatterStr = trimmedText.substring(3, endFrontmatterIndex).trim();
  const body = trimmedText.substring(endFrontmatterIndex + 3).trim();

  const metadata = {
    keyTakeaways: [],
    faqs: []
  };

  const lines = frontmatterStr.split('\n');
  let currentSection = null;
  let currentFaq = null;

  for (let i = 0; i < lines.length; i++) {
    const rawLine = lines[i];
    const line = rawLine.trim();
    if (!line) continue;

    if (line.startsWith('keyTakeaways:')) {
      currentSection = 'keyTakeaways';
      continue;
    }
    if (line.startsWith('faqs:')) {
      currentSection = 'faqs';
      continue;
    }

    if (currentSection === 'keyTakeaways' && line.startsWith('- ')) {
      let val = line.substring(2).trim();
      if ((val.startsWith('"') && val.endsWith('"')) || (val.startsWith("'") && val.endsWith("'"))) {
        val = val.substring(1, val.length - 1);
      }
      metadata.keyTakeaways.push(val);
      continue;
    }

    if (currentSection === 'faqs') {
      if (line.startsWith('- question:')) {
        let q = line.substring(11).trim();
        if ((q.startsWith('"') && q.endsWith('"')) || (q.startsWith("'") && q.endsWith("'"))) {
          q = q.substring(1, q.length - 1);
        }
        currentFaq = { question: q, answer: '' };
        metadata.faqs.push(currentFaq);
        continue;
      }
      if (line.startsWith('answer:') && currentFaq) {
        let a = line.substring(7).trim();
        if ((a.startsWith('"') && a.endsWith('"')) || (a.startsWith("'") && a.endsWith("'"))) {
          a = a.substring(1, a.length - 1);
        }
        currentFaq.answer = a;
        continue;
      }
    }

    const colonIdx = line.indexOf(':');
    if (colonIdx !== -1) {
      currentSection = null;
      const key = line.substring(0, colonIdx).trim();
      let val = line.substring(colonIdx + 1).trim();
      if ((val.startsWith('"') && val.endsWith('"')) || (val.startsWith("'") && val.endsWith("'"))) {
        val = val.substring(1, val.length - 1);
      }
      metadata[key] = val;
    }
  }

  return { metadata, body };
}

/**
 * Asynchronously loads and parses all markdown posts from src/content/blog/
 */
export async function getAllPostsAsync() {
  if (cachedPosts) return cachedPosts;
  if (loadingPromise) return loadingPromise;

  loadingPromise = (async () => {
    const keys = mdContext.keys();
    const posts = await Promise.all(
      keys.map(async (key) => {
        const fileUrl = mdContext(key);
        const res = await fetch(fileUrl);
        const text = await res.text();
        const { metadata, body } = parseFrontmatter(text);

        // Featured Image lookup from src/assets/blog/
        let image = null;
        if (metadata.featuredImage) {
          try {
            image = imgContext(`./${metadata.featuredImage}`);
          } catch (e) {
            const matchedKey = imgContext.keys().find(k => k.toLowerCase().includes(metadata.featuredImage.toLowerCase()));
            if (matchedKey) image = imgContext(matchedKey);
          }
        }

        const slug = metadata.slug || key.replace('./', '').replace('.md', '');

        return {
          title: metadata.title || '',
          slug: slug,
          date: metadata.date || '',
          author: metadata.author || 'Watt & Wattle',
          excerpt: metadata.excerpt || '',
          featuredImage: image,
          imageAlt: metadata.imageAlt || metadata.title || '',
          metaTitle: metadata.metaTitle || metadata.title || '',
          metaDescription: metadata.metaDescription || metadata.excerpt || '',
          rawContent: body,
          keyTakeaways: metadata.keyTakeaways || [],
          faqs: metadata.faqs || []
        };
      })
    );

    // Sort by date descending
    posts.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
    cachedPosts = posts;
    return posts;
  })();

  return loadingPromise;
}

export async function getPostBySlugAsync(slug) {
  const posts = await getAllPostsAsync();
  return posts.find((p) => p.slug === slug);
}

export async function getRelatedPostsAsync(currentSlug, limit = 3) {
  const posts = await getAllPostsAsync();
  return posts.filter((p) => p.slug !== currentSlug).slice(0, limit);
}
